/**
 * GRIPS-SUBSYSTEM (§5.11) — grip-drag → real-property translation.
 *
 * `StageGripDrag.ts` emits four pseudo-properties inside the
 * `properties/apply` payload when the user drags a grip:
 *
 *   gripDragWorldX  — dragged world X (string)
 *   gripDragWorldY  — dragged world Y (string)
 *   gripId          — the grip slot id ("start" / "end" / "midpoint" /
 *                     "quad-right" / "vertex-3" / "cp-1" / …)
 *   gripKind        — the GripKind enum value (informational)
 *
 * None of those are real editable entity properties, so the Command
 * Engine would silently drop them (the D1-r8 reopen reason: dragging a
 * grip did NOT change geometry). This pure translator interprets the
 * pseudo-properties against the entity's CURRENT geometry and returns the
 * REAL geometry-property patch the Command Engine understands
 * (`Start X` / `End X` / `Center X` / `Radius` / `Vertices` /
 * `Control points` / `Fit points` / …). The reducer then feeds that patch
 * through the normal `properties/apply` mutation path.
 *
 * Geometry is therefore ALWAYS committed through the Command Engine — the
 * translator only maps grip semantics to property names, it never mutates
 * the entity itself.
 *
 * UNDO NOTE (sub-grips·history): grip-drag commits as a real
 * `properties/apply`, the invertible command type. Undo of COMMON object
 * properties (Layer/Color/…) is already snapshotted by
 * `computeInverse`; undo of ENTITY-SPECIFIC GEOMETRY keys (Start X /
 * Radius / Vertices / …) is the pre-existing Phase 4.1.f deferral in
 * `geometryMutations.computeInverse` (COMMON_PROP_FIELD only) that affects
 * the Properties-panel geometry edit IDENTICALLY — it is NOT grip-specific.
 * When 4.1.f extends entity-specific inverse snapshotting, grip-drag undo
 * lights up with zero changes here (the command is already the right type).
 * See docs/test-gaps/sub-grips.md.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { DrawingDocument, DrawingEntity } from "@dxf-ai-editor/drawing-model";
import { bulgeMidpoint, parseBulgeList } from "@dxf-ai-editor/command-engine";

import { applyArrayGripDrag } from "../scene/arrayGripDrag";
import type { GripPoint } from "../scene/gripCalculation";
import { readRawCoord, readRawPointList, readRawZ } from "./geometryEditRawPoint";
import { translateEllipseGripDrag } from "./gripDragTranslateEllipse";

/** Keys StageGripDrag.ts injects into the `properties/apply` values bag. */
export const GRIP_DRAG_KEYS = [
  "gripDragWorldX",
  "gripDragWorldY",
  "gripId",
  "gripKind",
] as const;

/** True when the values bag is a grip-drag payload (carries the pseudo-keys). */
export function isGripDragPayload(values: Record<string, string>): boolean {
  return (
    "gripDragWorldX" in values &&
    "gripDragWorldY" in values &&
    "gripId" in values
  );
}

/**
 * ONE TRUTH (post-merge remediation of #493, round 2) — resolve the LIVE
 * pre-mutation document entity a grip-drag `properties/apply` must translate
 * against, for BOTH the shell reducer (`propertyReducer.ts`) and the
 * persisted document reducer (`geometryReducer.ts`). Shared here (not
 * duplicated per call site) so the typed-vs-legacy frame AND the geometry
 * precision the two reducers see are identical by construction.
 *
 * Precedence, in order:
 *
 *  1. A linear `geometryDocument.spaces` scan — AUTHORITATIVE when it finds
 *     the entity. The compact-cache resolver (`getEntityById`, when
 *     injected) reconstructs a fresh `DrawingEntity` from `Float32Array`
 *     coordinates on every call; `translateGripDrag` relies on this entity
 *     to preserve raw centers/endpoints/vertices/Z, so a Float32-rounded
 *     reconstruction must never be preferred over the full-precision
 *     document entity when the scan can already see it (Codex P2, #515
 *     round 2 — "preserve full document geometry when resolving grip
 *     drags").
 *  2. The injected `resolveDocumentEntity` (`getEntityById`, compact-cache
 *     first) — used ONLY as a fallback for the case the scan cannot see: a
 *     large/streamed import whose `geometryDocument.spaces` is slimmed
 *     (entities live only in the compact cache). Its presence there still
 *     correctly signals "this id resolves to a typed entity" for the Arc
 *     mirror-angle inverse-frame decision in `translateGripDrag`, even
 *     though the coordinates it carries are lossy — strictly better than
 *     the alternative (falling through to the legacy Y-mirror branch
 *     entirely).
 *  3. `undefined` — neither source has the entity (shell-only entity not
 *     yet in the geometry document, or no resolver injected and a slimmed
 *     scan miss); callers take the legacy/no-document-precision branch.
 */
export function resolveGripDragDocumentEntity(
  targetId: string,
  geometryDocument: DrawingDocument | undefined,
  resolveDocumentEntity: ((id: string) => DrawingEntity | undefined) | undefined,
): DrawingEntity | undefined {
  if (geometryDocument) {
    for (const space of geometryDocument.spaces) {
      const found = space.entities.find((e) => e.id === targetId);
      if (found) return found;
    }
  }
  return resolveDocumentEntity?.(targetId);
}

function num(v: string | undefined, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function fmt(n: number): string {
  // Match the producer's formatting precision (formatNumber rounds to a
  // stable string); keep it simple + lossless for round numbers.
  return String(n);
}

/** Parse a `"x,y;x,y"` coordinate-pair string into a point list. */
function readPoints(raw: string | undefined): Array<{ x: number; y: number }> {
  if (!raw) return [];
  return raw
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => p.split(","))
    .map(([x, y]) => ({ x: Number(x), y: Number(y) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}

/** Serialize a point list back to the `"x,y;x,y"` form. */
function writePoints(points: Array<{ x: number; y: number }>): string {
  return points.map((p) => `${fmt(p.x)},${fmt(p.y)}`).join(";");
}

/**
 * Translate a grip-drag pseudo-property bag into the real geometry
 * property patch for `entity`. Returns `null` when the grip is not
 * recognised for the entity type (the caller then drops the no-op rather
 * than committing garbage).
 */
export function translateGripDrag(
  entity: EntityPreview,
  values: Record<string, string>,
  // Codex P2-5 (#317) — the LIVE geometry-document entity (full precision).
  // Optional so callers that only need the preview (e.g. the shell preview side
  // where precision is display-rounded anyway) need not thread it; the
  // LWPOLYLINE / Spline vertex-precision path uses it when present to keep
  // UNTOUCHED points at their exact document coordinates.
  documentEntity?: DrawingEntity,
): Record<string, string> | null {
  const wx = num(values.gripDragWorldX);
  const wy = num(values.gripDragWorldY);
  const gripId = values.gripId ?? "";
  const p = entity.properties;

  switch (entity.type) {
    case "Line": {
      // Codex residual R2 (#317) — a 2-D endpoint / midpoint grip move must
      // PRESERVE the line's existing group-30/31 Z. Emit the unchanged endpoint
      // Z RAW from the live document entity (full precision) alongside the new
      // X/Y so the document patch re-emits the full point and `patchEntityById`
      // shallow-merge cannot flatten a non-zero elevation. `readRawZ` returns
      // undefined when there is no document Z (2-D line) — omit the key so the
      // edit stays a clean X/Y change (matches the Block insertion-grip path).
      const docLine =
        documentEntity?.type === "Line" ? documentEntity : undefined;
      const startZ = readRawZ(docLine?.start);
      const endZ = readRawZ(docLine?.end);
      if (gripId === "start") {
        return {
          "Start X": fmt(wx),
          "Start Y": fmt(wy),
          ...(startZ !== undefined ? { "Start Z": fmt(startZ) } : {}),
        };
      }
      if (gripId === "end") {
        return {
          "End X": fmt(wx),
          "End Y": fmt(wy),
          ...(endZ !== undefined ? { "End Z": fmt(endZ) } : {}),
        };
      }
      if (gripId === "midpoint") {
        // Midpoint grip translates the WHOLE line: both endpoints shift
        // by the delta from the old midpoint to the dragged point.
        // Codex P2-3 (#317·round-6) — read the endpoints from the RAW document
        // entity (full precision) instead of the formatNumber-rounded preview
        // strings (3 decimals). A midpoint drag is a pure TRANSLATE that writes
        // BOTH endpoints back, so sourcing them from the rounded preview
        // permanently rounded the line's endpoints / length on every move.
        // (Reuses the outer `docLine` resolved for the Z-preserve fix.)
        // Source the endpoints RAW from the live document entity (full
        // precision) via the shared invariant helper — never the
        // formatNumber-rounded preview string (GEOMETRY-EDIT-INVARIANTS).
        const sx = readRawCoord(docLine?.start.x, p["Start X"]);
        const sy = readRawCoord(docLine?.start.y, p["Start Y"]);
        const ex = readRawCoord(docLine?.end.x, p["End X"]);
        const ey = readRawCoord(docLine?.end.y, p["End Y"]);
        const oldMidX = (sx + ex) / 2;
        const oldMidY = (sy + ey) / 2;
        const dx = wx - oldMidX;
        const dy = wy - oldMidY;
        // Codex residual R2 (#317) — a midpoint translate is a pure XY shift; it
        // must keep BOTH endpoints' existing Z (a translate does not change
        // elevation). Emit each endpoint's RAW document Z so the patch re-emits
        // the full point and the shallow merge cannot flatten the elevation.
        return {
          "Start X": fmt(sx + dx),
          "Start Y": fmt(sy + dy),
          ...(startZ !== undefined ? { "Start Z": fmt(startZ) } : {}),
          "End X": fmt(ex + dx),
          "End Y": fmt(ey + dy),
          ...(endZ !== undefined ? { "End Z": fmt(endZ) } : {}),
        };
      }
      return null;
    }

    case "Circle": {
      // Codex P2 (#317·round-7) — source the center RAW from the live document
      // entity (full precision) for the radius computation. The preview
      // "Center X"/"Center Y" strings are formatNumber-rounded to 3 decimals, so
      // a high-precision center rounded into the recomputed |drag − center|
      // radius = a permanent geometry error persisted to the doc + DXF export.
      // Falls back to the (rounded) preview center only when no doc is threaded.
      const docCircle =
        documentEntity?.type === "Circle" ? documentEntity : undefined;
      // GEOMETRY-EDIT-INVARIANTS — source the center RAW from the document.
      const cx = readRawCoord(docCircle?.center.x, p["Center X"]);
      const cy = readRawCoord(docCircle?.center.y, p["Center Y"]);
      if (gripId === "center") {
        return { "Center X": fmt(wx), "Center Y": fmt(wy) };
      }
      // Any quadrant grip resizes the radius = distance(drag, center);
      // the center stays put (AutoCAD CIRCLE quadrant-grip behaviour).
      if (gripId.startsWith("quad-")) {
        const r = Math.hypot(wx - cx, wy - cy);
        if (r <= 0) return null;
        return { Radius: fmt(r) };
      }
      return null;
    }

    case "Arc": {
      // Codex P2 (#317·round-7) — source the center RAW from the live document
      // entity (full precision) for the angle (atan2) + radius computations. A
      // 3-decimal-rounded preview center re-aimed an endpoint angle / resized the
      // radius about the WRONG center → persistent geometry error. Falls back to
      // the (rounded) preview center only when no document entity is threaded.
      const docArc = documentEntity?.type === "Arc" ? documentEntity : undefined;
      // GEOMETRY-EDIT-INVARIANTS — source the center RAW from the document.
      const cx = readRawCoord(docArc?.center.x, p["Center X"]);
      const cy = readRawCoord(docArc?.center.y, p["Center Y"]);
      if (gripId === "center") {
        return { "Center X": fmt(wx), "Center Y": fmt(wy) };
      }
      if (gripId === "start" || gripId === "end") {
        // Endpoint grips re-aim the corresponding angle about the center. The
        // measured angle MUST use the SAME frame the dragged grip RENDERED in,
        // because `buildGripSet` picks the grip source per entity and the two
        // sources place the endpoint grip at vertically-MIRRORED points:
        //
        //   • cad-core typed source (`docArc` present): the grip renders at the
        //     canonical WCS `cy + r·sin(angle)` (`cad-core/grips.ts::arcGrips`,
        //     no display Y-flip) — ON the rendered arc. Inverse: measure the
        //     angle in that WCS frame, `atan2(wy − cy, wx − cx)`.
        //   • legacy `calculateGrips` fallback (`docArc` absent — the grip
        //     resolver is omitted or an id does not resolve to a typed entity,
        //     e.g. a shell-only arc that is not in the geometry document): the
        //     grip renders at the screen-space `cy − r·sin(angle)` Y-mirror.
        //     Its self-consistent inverse negates the Y delta, `atan2(cy − wy,
        //     wx − cx)`, so dragging that visible (mirrored) grip to its OWN
        //     displayed position PRESERVES the angle instead of writing the
        //     360−angle mirror (Codex P2 — the advertised zero-behaviour legacy
        //     fallback must not corrupt arc angles when typed resolution is
        //     unavailable; a 90° start grip shown at (0,−10) must stay 90°, not
        //     flip to 270°).
        //
        // `docArc` presence faithfully replays `buildGripSet`'s per-entity
        // resolver decision: the authoritative geometry reducer resolves
        // `preMutationDocEntity` from the SAME live `document.spaces` the grip-
        // source resolver (`resolveTypedEntityById`) reads, so present ⟺ the
        // arc rendered its grips from cad-core (WCS). The shell preview path
        // (`propertyReducer`, no doc threaded) takes the legacy branch; that
        // result is transient — `syncShellStateFromGeometryDocument` overwrites
        // it from the geometry document — and stays correct for a shell-only
        // (legacy-rendered) arc.
        const angleDeltaY = docArc ? wy - cy : cy - wy;
        const angDeg = (Math.atan2(angleDeltaY, wx - cx) * 180) / Math.PI;
        const norm = ((angDeg % 360) + 360) % 360;
        return gripId === "start"
          ? { "Start angle": fmt(norm) }
          : { "End angle": fmt(norm) };
      }
      if (gripId === "arc-midpoint") {
        // Midpoint (radius) grip resizes the radius; center + angles kept.
        const r = Math.hypot(wx - cx, wy - cy);
        if (r <= 0) return null;
        return { Radius: fmt(r) };
      }
      return null;
    }

    // GRIPS-DRAG-MORE-TYPES — ELLIPSE center/major-axis/minor-axis grips.
    // Split into `gripDragTranslateEllipse.ts` (file-size discipline — see
    // that module's doc for the full per-grip semantics).
    case "Ellipse":
      return translateEllipseGripDrag(p, documentEntity, gripId, wx, wy);

    case "LWPOLYLINE": {
      // Codex P2-5 (#317) — base the rewritten list on the RAW document
      // vertices (full precision) so untouched vertices keep their exact
      // coordinates; only the dragged vertex/segment changes. Falls back to the
      // rounded preview list when no document entity is threaded.
      const docVerts =
        documentEntity?.type === "LWPOLYLINE"
          ? documentEntity.vertices
          : undefined;
      const verts = readRawPointList(readPoints(p.Vertices), docVerts);
      if (verts.length === 0) return null;
      if (gripId.startsWith("vertex-")) {
        const i = Number(gripId.slice("vertex-".length));
        if (!Number.isInteger(i) || i < 0 || i >= verts.length) return null;
        verts[i] = { x: wx, y: wy };
        return { Vertices: writePoints(verts) };
      }
      if (gripId.startsWith("seg-mid-")) {
        // Segment-midpoint stretch: move BOTH endpoints of the segment by
        // the delta from the old midpoint to the dragged point (the
        // closing segment wraps last→first).
        const i = Number(gripId.slice("seg-mid-".length));
        if (!Number.isInteger(i) || i < 0 || i >= verts.length) return null;
        const j = i + 1 >= verts.length ? 0 : i + 1;
        const v1 = verts[i]!;
        const v2 = verts[j]!;
        // The grip RENDERS at `bulgeMidpoint(v1, v2, bulge[i])` (see
        // gripCalculation.ts buildLwpolylineGrips), which for a bulged (arc)
        // segment sits ON the arc bump, not on the chord. Measure the drag
        // delta from that SAME midpoint — bulge i applies to the segment
        // STARTING at vertex i (AutoCAD stores bulge on the start vertex).
        // Straight segments (bulge 0) reduce to the chord midpoint, so this
        // is identical to the previous behaviour for them.
        // Codex P2-5 (#317·round-6) — read the segment bulge from the RAW
        // document vertices (full precision) instead of the formatNumber-rounded
        // `p.Bulges` preview string (3 decimals). With a high-precision bulge
        // the rounded value placed `oldMid` on the WRONG arc point, so the drag
        // delta (and thus both shifted endpoints) carried a small persistent
        // error. Falls back to the parsed preview bulge when no document entity
        // is threaded (the same lossy-but-correct behaviour as before).
        const bulgeI =
          docVerts && docVerts.length === verts.length
            ? (docVerts[i]?.bulge ?? 0)
            : (parseBulgeList(p.Bulges)[i] ?? 0);
        const oldMid = bulgeMidpoint(v1, v2, bulgeI);
        const oldMidX = oldMid.x;
        const oldMidY = oldMid.y;
        const dx = wx - oldMidX;
        const dy = wy - oldMidY;
        verts[i] = { x: v1.x + dx, y: v1.y + dy };
        verts[j] = { x: v2.x + dx, y: v2.y + dy };
        return { Vertices: writePoints(verts) };
      }
      return null;
    }

    case "Rectangular Associative Array": {
      // Reuse the existing array multifunctional-grip drag translator
      // (formerly orphaned — only the SVG stage called it). It maps a
      // count / spacing grip drag to the real "Columns" / "Rows" /
      // "Column spacing" / "Row spacing" property patch. The grip role +
      // constraint axis are reconstructed from the grip id (the id IS the
      // role for array grips: column-count / row-count / column-spacing /
      // row-spacing). The origin grip is a free positional move handled by
      // the generic positional fall-through (not here).
      const role = gripId as GripPoint["gripRole"];
      if (
        role !== "column-count" &&
        role !== "row-count" &&
        role !== "column-spacing" &&
        role !== "row-spacing"
      ) {
        // origin / unknown → free positional move of the base point.
        if (gripId === "origin") {
          const hasBase = "Base X" in p || "Base Y" in p;
          return hasBase
            ? { "Base X": fmt(wx), "Base Y": fmt(wy) }
            : { "Origin X": fmt(wx), "Origin Y": fmt(wy) };
        }
        return null;
      }
      const constraintAxis: GripPoint["constraintAxis"] =
        role === "column-count" || role === "column-spacing"
          ? "column"
          : "row";
      const grip: GripPoint = {
        id: gripId,
        entityId: entity.id,
        x: 0,
        y: 0,
        propertyX: "",
        propertyY: "",
        gripRole: role,
        constraintAxis,
      };
      let patch: Record<string, string> | null = null;
      applyArrayGripDrag(grip, { x: wx, y: wy }, entity, (p2) => {
        patch = p2;
      });
      return patch;
    }

    case "Spline": {
      // Codex P2-5 (#317) — source untouched control / fit points from the RAW
      // document point list so only the dragged point loses its old precision;
      // every other point round-trips at full document precision.
      const docCp =
        documentEntity?.type === "Spline"
          ? documentEntity.controlPoints
          : undefined;
      const docFp =
        documentEntity?.type === "Spline"
          ? documentEntity.fitPoints
          : undefined;
      if (gripId.startsWith("cp-")) {
        const cps = readRawPointList(readPoints(p["Control points"]), docCp);
        const i = Number(gripId.slice("cp-".length));
        if (!Number.isInteger(i) || i < 0 || i >= cps.length) return null;
        cps[i] = { x: wx, y: wy };
        return { "Control points": writePoints(cps) };
      }
      if (gripId.startsWith("fit-")) {
        const fps = readRawPointList(readPoints(p["Fit points"]), docFp);
        const i = Number(gripId.slice("fit-".length));
        if (!Number.isInteger(i) || i < 0 || i >= fps.length) return null;
        fps[i] = { x: wx, y: wy };
        return { "Fit points": writePoints(fps) };
      }
      return null;
    }

    // Codex P2 (S12b) — POINT `location` grip = pure translate. cad-core now
    // sources a POINT's grip from the typed model (`cad-core/grips.ts::
    // pointGrips`, a single `location` grip absent from the legacy
    // `calculateGrips`), so this grip is newly VISIBLE + drag-able and must
    // commit real geometry. "Position X"/"Position Y" are typed-writer-owned
    // PERSISTABLE labels (geometryReducer Step 1d; propertyTranslator.ts's
    // `Point` set) that map back to `location`, so — exactly like the Block
    // insertion grip below — the move persists to the geometry doc + DXF export
    // + undo, NOT a shell-only change (contrast Text/MText insertion, which
    // have NO document translator mapping and therefore stay a clean no-op in
    // the `default` branch). The point's existing group-30 Z is preserved by
    // emitting the RAW document Z (mirrors the Line/Block Z-preserve fixes): a
    // 2-D point (no doc Z) emits only X/Y; a 3-D point keeps its elevation.
    case "Point": {
      if (gripId === "location") {
        const docPoint =
          documentEntity?.type === "Point" ? documentEntity : undefined;
        const locZ = readRawZ(docPoint?.location);
        return {
          "Position X": fmt(wx),
          "Position Y": fmt(wy),
          ...(locZ !== undefined ? { "Position Z": fmt(locZ) } : {}),
        };
      }
      return null;
    }

    // Codex P2-4 (#317·round-6) — BLOCK insertion grip = pure translate. The
    // Block insert exposes a single `insertion` grip (gripCalculation.ts) at
    // (Insertion X, Insertion Y); dragging it is a pure move to the dragged
    // world point — a trivial-correct patch (no axis/defpoint maths). This is
    // the ONLY rendered-grip type beyond the existing cases that ALSO has a
    // full document round-trip: `translateExtendedEntityPropertyChanges`'s Block
    // path maps Insertion X/Y → `insertionPoint`, so the move persists to the
    // geometry doc + DXF export + undo.
    case "Block": {
      if (gripId === "insertion") {
        // Codex P2 (#317·round-7) — the insertion grip is a 2-D move: it emits
        // Insertion X/Y only. The block's existing insertionPoint.z (DXF group
        // 30, non-zero for an elevated 3-D block) is PRESERVED downstream by the
        // Block document translator (translateExtendedEntityPropertyChanges),
        // which sources Z RAW from the live document entity when rebuilding
        // `insertionPoint` — so a 2-D grip drag changes X/Y and keeps Z.
        return { "Insertion X": fmt(wx), "Insertion Y": fmt(wy) };
      }
      return null;
    }

    // GRIPS-DRAG-MORE-TYPES — TEXT / MTEXT insertion grip = pure translate.
    // INSTRUCTION-FRESHNESS CORRECTION: the previous comment here (deleted)
    // claimed Text/MText "Insertion X"/"Insertion Y" had NO document
    // translator mapping. That is no longer true (and per-HEAD verification
    // suggests it may never have been, post-S15) — `descriptors/text.ts`'s
    // `textDescriptors`/`mtextDescriptors` register `coord("Insertion X",
    // "insertionPoint.x", …)` / `coord("Insertion Y", "insertionPoint.y", …)`,
    // and both "Text"/"MText" are in `TYPED_EXTENDED_GEOMETRY_KINDS`
    // (typedPropertyWriter.ts) — so the edit already routes through the S15
    // typed writer to `insertionPoint`, persisting to the geometry doc + DXF
    // export + undo exactly like the Block insertion grip above. The prior
    // no-op was a translator gap (this switch never emitted the patch), not a
    // missing document write path.
    //
    // Both TEXT and MTEXT expose exactly ONE selectable/rendered grip today —
    // "insertion" (`cad-core/grips.ts::textGrips`/`mtextGrips`, and the legacy
    // `gripCalculation.ts` fallback agree: neither renders a width/height or
    // column-stretch grip). AutoCAD's real MTEXT grip set additionally shows a
    // column-width stretch handle + a rotate grip; adding those is a
    // render-side change (a NEW grip kind in `gripCalculation.ts` +
    // `cad-core/grips.ts` + `StageGripDrag.ts`'s hit-test), not a drag-COMMIT
    // gap this translator can close — tracked as documented follow-up
    // (docs/test-gaps/sub-grips.md).
    //
    // `insertionPoint` is a plain 2-D point on both entities
    // (`drawingPointSchema`, no `z` field at all — unlike Line/Circle/Point/
    // Block's `drawingPointOptionalZSchema`), so there is no elevation to
    // preserve here.
    case "Text":
    case "MText": {
      // Codex P2 (GRIPS-DRAG-MORE-TYPES review, round 2) — a TEXT
      // insertion-grip drag is a whole-entity TRANSLATE; AutoCAD stores a
      // SEPARATE group-11/21 `alignmentPoint` for justified (non-default
      // hJustification/vJustification) TEXT, so leaving it stale would
      // corrupt the displayed justification (every other whole-entity
      // transform moves both points together —
      // `geometryTransforms.ts::transformEntityCoords`'s "Text" case). This
      // translator does NOT compute that shift itself: "Insertion X"/
      // "Insertion Y" route through the `textInsertionMoveMutator`
      // SIDE-EFFECT (`descriptors/text.ts`/`sideEffectMutators.ts`), which
      // shifts `alignmentPoint` by the same delta server-side, GATED on the
      // document entity genuinely carrying one — a bare pure-translate patch
      // here is sufficient, and correct for MTEXT too (no `alignmentPoint`
      // field at all, so the mutator is a no-op extra check for that kind).
      if (gripId === "insertion") {
        return { "Insertion X": fmt(wx), "Insertion Y": fmt(wy) };
      }
      return null;
    }

    // Codex PR #785 round 1, F1 — ACAD_TABLE insertion grip = pure translate.
    // `cad-core/grips.ts::tableGrips` renders exactly one grip on a table (the
    // insertion point AutoCAD uses to MOVE it — a table IS a block reference,
    // `100 AcDbBlockReference`), and until this arm existed the switch fell to
    // `default: null`, so the advertised grip dragged and wrote nothing.
    //
    // "Position X"/"Position Y" are the descriptor labels
    // (`descriptors/legacyPolylineTableOle.ts`, `coord(… "insertionPoint.x")`)
    // that BOTH preview factories emit and that the typed extended-geometry
    // writer OWNS for this kind, so the move persists to the geometry document
    // + DXF export + undo exactly like the Block insertion grip above — not a
    // shell-only change.
    //
    // No "Position Z" is emitted: the drag is a 2-D move and the descriptor
    // pair carries `zPreserve`, so the group-30 Z of an elevated table is kept
    // by the writer rather than flattened (the same reasoning the Block /
    // Line / Point Z-preserve paths use).
    case "Table": {
      if (gripId === "insertion") {
        return { "Position X": fmt(wx), "Position Y": fmt(wy) };
      }
      return null;
    }

    // Codex PR #785 round 1, F1 — OLE2FRAME frame-corner grips.
    //
    // `cad-core/grips.ts::oleFrameGrips` renders the four frame corners
    // AutoCAD shows on an OLE object, and the frame rectangle IS the entity's
    // whole geometry (groups 10/20 upper-left + 11/21 lower-right). Dragging a
    // corner moves exactly the two stored coordinates that corner is built
    // from, leaving the OPPOSITE corner pinned — AutoCAD's own corner-grip
    // behaviour, and the reason each arm writes ONE x and ONE y rather than
    // translating the whole frame:
    //
    //   upper-left  → upperLeft.x  + upperLeft.y
    //   upper-right → lowerRight.x + upperLeft.y
    //   lower-right → lowerRight.x + lowerRight.y
    //   lower-left  → upperLeft.x  + lowerRight.y
    //
    // The two MIXED corners are why the grip's single-field `patch` hint
    // cannot drive this (see `oleFrameGrips`' doc) — the mapping is per-id
    // here. All four labels are descriptor-owned coords for this kind, so the
    // resize persists to the document + export + undo. Each descriptor carries
    // `zPreserve`, so the corners' group-30/31 Z survives a 2-D drag.
    case "OLE Frame": {
      // A `Map` (not an object literal) so an id that happens to name an
      // inherited `Object.prototype` member — "constructor", "toString" — can
      // never resolve to a bogus label pair; an unknown id falls through to the
      // same clean no-op every other unhandled grip takes.
      const CORNER_LABELS = new Map<string, readonly [string, string]>([
        ["upper-left", ["Upper left X", "Upper left Y"]],
        ["upper-right", ["Lower right X", "Upper left Y"]],
        ["lower-right", ["Lower right X", "Lower right Y"]],
        ["lower-left", ["Upper left X", "Lower right Y"]],
      ]);
      const labels = CORNER_LABELS.get(gripId);
      if (!labels) return null;
      return { [labels[0]]: fmt(wx), [labels[1]]: fmt(wy) };
    }

    // Codex P2-4 (#317·round-6) — every OTHER selected-and-rendered grip
    // (Dimension defpoints, Hatch/Viewport/Revision-Cloud corners, Multileader
    // vertices) is a CLEAN no-op: returning `null` makes the SHELL reducer
    // take its `noShellMutation` branch (propertyReducer) and the DOCUMENT
    // reducer apply an empty patch (geometryReducer `?? {}`), so NO geometry
    // changes; the grouped-undo batch then commits empty (editorStore
    // commit-batch forwards.length===0 path → no phantom history entry). So an
    // unhandled grip drag NEVER corrupts the entity or pollutes history — it
    // simply does nothing until the per-type drag lands (follow-up slice
    // GRIPS-DRAG-MORE-TYPES-2, docs/test-gaps/sub-grips.md — Ellipse and
    // Text/MText shipped in GRIPS-DRAG-MORE-TYPES above; Dimension /
    // Multileader / Hatch / Viewport / Revision-Cloud remain deferred, each
    // needing non-trivial per-type multi-grip semantics AutoCAD implements
    // distinctly per dimension type / boundary topology). This is NOT a silent
    // fallthrough: it is the explicit, AutoCAD-safe interim behaviour for grip
    // kinds whose correct drag semantics are non-trivial and deferred per scope.
    //
    // The heavy `POLYLINE` is NOT in that deferred set: it reaches this branch
    // because `cad-core/grips.ts::heavyPolylineGrips` renders NO grips at all
    // (its vertex list has no typed writer — see that function's doc), so
    // there is no advertised control to leave inert. When the PEDIT slice
    // gives `vertices` a writer, the grips return and a "Polyline" arm lands
    // with them.
    default:
      return null;
  }
}
